import {
  httpResetGoogleAuthenticator,
  httpRestoreUserAccess,
  httpUserSecurity,
} from "api/admin/user";
import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { showDanger, showSuccess } from "utilities/misc";

const UserSecurity = ({ userId }: { userId: number }) => {
  const client = useQueryClient();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const { data, isLoading, isError, refetch } = useQuery(
    ["user-security", userId],
    () => httpUserSecurity(userId),
    { enabled: Number.isSafeInteger(userId) && userId > 0 },
  );
  const act = async (action: "restore" | "reset") => {
    if (inFlight.current || !data || isError || note.trim().length < 3) return;
    const question =
      action === "restore"
        ? "Restore this user's access? They will need to sign in again. Google Authenticator will stay unchanged."
        : "Reset this user's Google Authenticator? This signs them out everywhere and removes trust from all their devices. Any security restriction stays in place. Confirm you have verified their identity.";
    if (!window.confirm(question)) return;
    inFlight.current = true;
    setBusy(true);
    try {
      await (
        action === "restore"
          ? httpRestoreUserAccess
          : httpResetGoogleAuthenticator
      )(userId, note.trim());
    } catch (error) {
      showDanger(
        (error as any)?.message || "The security action could not be completed",
      );
      inFlight.current = false;
      setBusy(false);
      return;
    }
    setNote("");
    showSuccess(
      action === "restore"
        ? "Account access restored"
        : "Google Authenticator reset",
    );
    try {
      await refetch({ throwOnError: true });
      await client.invalidateQueries(["devices", "user", userId]);
    } catch {
      showDanger(
        "Action completed, but the status could not refresh. Reload this page.",
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  const disabled =
    busy ||
    note.trim().length < 3 ||
    note.trim().length > 2000 ||
    !data ||
    isError;
  return (
    <section className="pt-6 mt-8 border-t border-orange-200">
      <h5 className="font-semibold text-gray-900">Account security</h5>
      {isLoading ? (
        <p className="mt-3 text-sm">Loading security status…</p>
      ) : isError || !data ? (
        <p className="mt-3 text-sm text-red-700">
          Security status could not be loaded.
          <button className="ml-2 underline" onClick={() => refetch()}>
            Retry
          </button>
        </p>
      ) : (
        <>
          <p className="mt-3 text-sm">
            Account access:{" "}
            {data.securityRestrictedAt
              ? "Security restricted"
              : "No security restriction"}
          </p>
          <p className="mt-2 text-sm">
            Google Authenticator:{" "}
            {data.googleAuthenticatorEnabled
              ? "Enabled"
              : data.googleAuthenticatorPending
                ? "Setup pending"
                : "Disabled"}
          </p>
          <label
            className="block mt-4 text-sm"
            htmlFor={`security-reason-${userId}`}
          >
            Reason / case reference
          </label>
          <textarea
            id={`security-reason-${userId}`}
            value={note}
            disabled={busy}
            maxLength={2000}
            onChange={(event) => setNote(event.target.value)}
            className="w-full p-2 mt-2 border border-orange-200 rounded-lg"
          />
          <div className="flex flex-wrap gap-3 mt-3">
            <button
              disabled={disabled || !data.securityRestrictedAt}
              onClick={() => act("restore")}
              className="px-3 py-2 text-sm text-white bg-black rounded-lg disabled:opacity-40"
            >
              Restore access
            </button>
            <button
              disabled={
                disabled ||
                !(
                  data.googleAuthenticatorEnabled ||
                  data.googleAuthenticatorPending
                )
              }
              onClick={() => act("reset")}
              className="px-3 py-2 text-sm text-white bg-black rounded-lg disabled:opacity-40"
            >
              Reset Google Authenticator
            </button>
          </div>
          {busy && (
            <p role="status" className="mt-2 text-sm">
              Applying security change…
            </p>
          )}
        </>
      )}
    </section>
  );
};
export default UserSecurity;
